import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react'
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query'

import API from '@shared/services/src/API'
import { pageParam, QK } from '@shared/utils'

import { useStatusChangeConfirmation } from '../components/useStatusChangeConfirmation'
import { useCommonFilters, useStatusUpdate } from '../Kanban.hooks'

const LIMIT = 30

const TasksContext = createContext(undefined)

export function useTasks() {
  const context = useContext(TasksContext)
  if (!context) {
    throw new Error('useTasks must be used within a TasksProvider')
  }
  return context
}

export function TasksProvider({ children }) {
  const state = useState({})

  return <TasksContext.Provider value={state}>{children}</TasksContext.Provider>
}

export function useDragHandler() {
  const moveTask = useTaskMove()
  const requestStatusChange = useStatusChangeConfirmation()

  return useCallback(
    ({ over, active }) => {
      if (!over) return

      const item = active.data.current.item
      const oldStatus = item.status
      const newStatus = over.id

      if (newStatus === oldStatus) return

      requestStatusChange(item, newStatus, {
        onConfirm: (reason) => moveTask({ id: active.id, oldStatus, newStatus, reason }),
      })
    },
    [moveTask, requestStatusChange]
  )
}

function useTaskMove() {
  const [, setTasks] = useTasks()
  const updateStatus = useStatusUpdate()

  return useCallback(
    ({ id, oldStatus, newStatus, reason }) => {
      // Optimistic update
      setTasks((tasks) => {
        const task = tasks[oldStatus]?.find((task) => task.id === id)
        if (!task) return tasks

        return {
          ...tasks,
          [oldStatus]: tasks[oldStatus].filter((task) => task.id !== id),
          [newStatus]: [...tasks[newStatus], { ...task, status: newStatus }].sort((a, b) => a.id - b.id),
        }
      })

      updateStatus.mutate({ id, newStatus, oldStatus, reason })
    },
    [setTasks, updateStatus]
  )
}

export function useColumn(status, role) {
  const [{ priority, users, kind, provider, createdBy, search }] = useCommonFilters()
  const [tasks, setTasks] = useTasks()

  const query = useMemo(
    () => ({
      priority,
      role,
      assignee_ids: users,
      author_ids: createdBy ? [createdBy] : undefined,
      provider_id: provider,
      search_term: search,
      kind,
      sort: 'identifier',
      order: 'asc',
      board_view: true,
      limit: LIMIT,
    }),
    [createdBy, kind, priority, provider, role, search, users]
  )

  const { data, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: QK.tasks.list({ ...query, status }),
    queryFn: ({ pageParam }) => API.tasks.list({ ...query, status, offset: pageParam * LIMIT }),
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
    initialPageParam: 0,
    getNextPageParam: pageParam(LIMIT),
  })

  useLayoutEffect(() => {
    if (!data) return
    setTasks((prev) => ({ ...prev, [status]: data.pages.flat() }))
  }, [data, status]) // eslint-disable-line react-hooks/exhaustive-deps

  return useMemo(() => {
    return {
      data: tasks[status],
      isPending,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isPending, status, tasks])
}
